<template>
  <div>
    <!-- HEADER -->
    <div class="petition-counter" v-if="showCounter">
      <div class="edo-progress-bar">
        <div class="progress-bar-wrapper">
          <div
            class="progress-bar-inner"
            :style="{ width: 100 - progress + '%' }"
          ></div>
        </div>
        <div
          class="edo-tree-icon"
          :style="{ left: 'calc(' + progress + '% - 32px)' }"
        >
          <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4119/dist/assets/img/edo-tree-icon.svg" />
        </div>
      </div>
      <h2 v-if="showCounter">
        <count-to
          ref="counter"
          :startVal="startcounter"
          :end-val="currentCounter"
          :autoplay="false"
          :duration="7000"
          @callbackCounter="updatePercentage"
        ></count-to>
      </h2>
      <h5 id="form-scroll-to">{{ counterTitle }}</h5>
    </div>

    <!-- FORM -->
    <div class="petition-form wwf-form light">
      <div class="flip-wrapper" :class="{ 'formValidation-shake': isShake }">
        <p class="form-intro" v-if="intro">{{ intro }}</p>

        <!-- READ LETTER TOP -->
        <input
          type="button"
          class="read-letter"
          :value="viewMessageButtonValue"
          v-on:click="openReadLetterPage"
          v-if="viewMessagePosition === 'top'"
        />

        <div>
          <!-- FIRST NAME -->
          <input
            name="firstName"
            type="text"
            v-model="firstName"
            v-validate="{ required: true }"
            :class="{ 'border-red': errors.has('firstName') }"
            placeholder="First name"
            autocomplete="given-name"
            v-on:focus="logFirstTouch('first name')"
          />
          <span class="wwf-alert">
            <p v-if="errors.has('firstName')">{{ errors.first("firstName") }}</p>
          </span>

          <!-- LAST NAME -->
          <input
            name="lastName"
            type="text"
            v-model="lastName"
            v-validate="{ required: true }"
            :class="{ 'border-red': errors.has('lastName') }"
            placeholder="Last name"
            autocomplete="family-name"
            v-on:focus="logFirstTouch('last name')"
          />
          <span class="wwf-alert">
            <p v-if="errors.has('lastName')">{{ errors.first("lastName") }}</p>
          </span>

          <!-- EMAIL -->
          <div class="with-input-loading">
            <input
              name="email"
              type="email"
              v-model="email"
              v-validate="{
                required: true,
                petitionEmailIsValid: true,
                email: true,
              }"
              :class="{ 'border-red': errors.has('email') }"
              placeholder="Email"
              @input="onEmailInput()"
              v-on:focus="logFirstTouch('email')"
            />
            <div
              class="input-loading"
              v-if="emailCheckSensis.checkEmailLoading"
            >
              <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4119/dist/assets/img/loading-tail-spin.svg" />
            </div>
            <span class="wwf-alert">
              <p v-if="errors.has('email')">{{ errors.first("email") }}</p>
            </span>
          </div>

          <!-- POST CODE -->
          <input
            v-if="showPostcode"
            name="postcode"
            :type="country == 'Australia' ? 'number' : 'text'"
            v-model="postcode"
            placeholder="Postcode"
            v-validate="{ required: true, alpha_num: true, length: country == 'Australia' ? 4 : false }"
            :class="{ 'border-red': errors.has('postcode') }"
            v-on:focus="logFirstTouch('postcode')"
          />
          <span class="wwf-alert">
            <p v-if="errors.has('postcode')">{{ errors.first("postcode") }}</p>
          </span>

          <!-- PHONE NUMBER -->
          <input
            v-if="showPhoneNumber"
            name="phoneNumber"
            type="tel"
            v-model="phoneNumber"
            placeholder="Phone Number"
            v-validate="{
              required: isPhoneMandatory,
              petitionPhoneNumberIsValid: true,
            }"
            :class="{ 'border-red': errors.has('phoneNumber') }"
            v-on:focus="logFirstTouch('phone')"
          />
          <span class="wwf-alert">
            <p v-if="errors.has('phoneNumber')">{{ errors.first("phoneNumber") }}</p>
          </span>

          <!-- STATE -->
          <select
            v-if="showState"
            name="state"
            v-model="state"
            v-validate="'required'"
            :class="{ 'border-red': errors.has('state') }"
            placeholder="State"
          >
            <option value="">State</option>
            <option value="ACT">ACT</option>
            <option value="NSW">NSW</option>
            <option value="NT">NT</option>
            <option value="QLD">QLD</option>
            <option value="TAS">TAS</option>
            <option value="VIC">VIC</option>
            <option value="SA">SA</option>
            <option value="WA">WA</option>
            <option value="Outside Australia">Outside Australia</option>
          </select>

          <!-- COUNTRY -->
          <select
            v-if="showCountry"
            name="country"
            v-model="country"
            v-validate="'required'"
            :class="{ 'border-red': errors.has('country') }"
            autocomplete="country"
            placeholder="Country"
          >
            <option
              v-for="(countries, index) in countriesList"
              :value="countries.name"
            >
              {{ countries.name }}
            </option>
          </select>

          <!-- MESSAGE -->
          <textarea
            v-if="showMessage"
            name="message"
            placeholder="Your message. Writing a personalised message makes it harder for our politicians to ignore"
            v-model="message"
            maxlength="255"
            :class="{ 'border-red': errors.has('message') }"
            v-on:focus="logFirstTouch('message')"
          ></textarea>
          <p v-if="showMessage" class="word-count">
            {{ 255 - (message ? message.length : 0) + " Char" }}
          </p>

          <!-- SUBMIT -->
          <input
            type="button"
            class="btn-submit"
            v-on:click="save()"
            :disabled="isSaving"
            :value="isSaving ? 'Loading...' : signButtonValue"
          />

          <!-- READ LETTER BOTTOM -->
          <input
            type="button"
            class="read-letter"
            :value="viewMessageButtonValue"
            v-on:click="openReadLetterPage"
            v-if="viewMessagePosition === 'bottom'"
          />

          <!-- MESSAGE PURE -->
          <span class="alert-box" v-show="!isPure">
            No bad words! Please change your message.
          </span>

          <!-- PRIVACY STATEMENT -->
          <div class="privacy-statement">
            <p>
              By signing you accept
              <a href="/privacy-policy/privacy-policy" target="_blank">
                WWF-Australia&rsquo;s Privacy Policy.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase";
import { Validator } from "vee-validate";
import countTo from "vue-count-to";
import { getParameterByName, getCountriesList } from "../../helpers";

const { detect } = require("detect-browser");
const browser = detect();

export default {
  name: "PetitionBio",
  components: {
    countTo,
  },
  created: async function () {
    // Define some mandatory fields with the query params
    let uri = window.location.search.substring(1);
    if (uri.length > 0) {
      let params = new URLSearchParams(uri);

      // utm_source
      this.utmSourceParam = params.get("utm_source");
      this.isFromRokt =
        this.utmSourceParam && this.utmSourceParam.includes("rokt")
          ? true
          : false;
    }

    // Check if there are some query string in URL to prefill the form, Query from donate widget
    let q = getParameterByName("q");
    if (q) {
      let decodeq = "?" + window.atob(q);
      console.log("decodeq", decodeq);
      this.firstName = getParameterByName("firstname", decodeq);
      this.lastName = getParameterByName("lastname", decodeq);
      this.email = getParameterByName("email", decodeq);
      this.postcode = getParameterByName("postcode", decodeq);
      this.phoneNumber = getParameterByName("phone", decodeq);
    }

    let o = getParameterByName("o");
    if (o) {
      this.origin = o;
    }

    // Define if we show the counter
    if (this.showCounter) {
      this.subscriptions.push(
        await firebase
          .firestore()
          .collection("campaigns")
          .doc(this.datalayerName)
          .onSnapshot(
            (snapshot) => {
              this.currentCounter = snapshot.data().count;
              this.$refs.counter.count(this.currentCounter);
            },
            (error) => {
              console.error(err);
            }
          )
      );
    }

    // Check the email
    let petitionEmailIsValid = (email) => {
      return new Promise((resolve, reject) => {
        Promise.all([this.checkEmailDuplicate(), this.checkEmail()])
          .then((results) => {
            for (const r of results) {
              if (!r.valid) {
                return resolve(r);
              }
            }
            this.errors.remove("email");
            resolve({ valid: true });
          })
          .catch((err) => {
            console.error(err);
            this.errors.remove("email");
            resolve({ valid: true });
          });
      });
    };

    Validator.extend("petitionEmailIsValid", {
      validate: petitionEmailIsValid,
      getMessage: (field, params, data) => data.message,
    });

    // Check Phone Number
    let petitionPhoneNumberIsValid = (phoneNumber) => {
      const validationRegex = /^(\+?61|0)([0-9]{8,9})/;
      if (!phoneNumber.replace(/ /g, "").match(validationRegex)) {
        return {
          valid: false,
          data: { message: "Invalid phone number" },
        };
      } else {
        return {
          valid: true,
        };
      }
    };

    Validator.extend("petitionPhoneNumberIsValid", {
      validate: petitionPhoneNumberIsValid,
      getMessage: (field, params, data) => data.message,
    });
  },
  mounted: function () {
    if (this.showCounter && this.$refs.counter) {
      this.$refs.counter.start();
    }

    // Fetch the content of the messages
    if (this.letterMessage) {
      this.$store
        .dispatch("getContentMessageById", this.letterMessage)
        .then((value) => {
          if (value) {
            this.letterContent = value.content;
          } else {
            this.letterContent = "";
          }
        });
    }
  },
  beforeDestroy: function () {
    // Unsubscript from all subscriptions
    for (const s of this.subscriptions) {
      s();
    }
  },
  computed: {},
  watch: {},
  props: {
    // Header
    intro: {
      type: String,
      required: false,
    },
    counterLimit: {
      type: Number,
      default: 10000,
    },
    counterTitle: {
      type: String,
      default: "Australians have sent an urgent message! Add your voice now.",
    },

    datalayerName: {
      type: String,
      required: true,
    },

    // Letter content
    letterTitle: {
      type: String,
      required: true,
    },
    letterMessage: {
      type: String,
      required: false,
    },

    // Thank you content
    thankYouTitle: {
      type: String,
      default: "Thank you!",
    },
    thankYouMessage: {
      type: String,
      default: "Thank you for adding your voice!",
    },

    // Lead (Marketo, IVE, ...)
    programName: {
      type: String,
      required: true,
    },
    keepData: {
      type: Boolean,
      default: true,
    },
    scheduledLead: {
      type: Boolean,
      default: false,
    },
    sendToIve: {
      type: Boolean,
      default: false,
    },
    iveSupplier: {
      type: String,
      required: false,
    },
    iveSupplierPhoneNotMandatory: {
      type: String,
      required: false,
    },
    iveSource: {
      type: String,
      required: false,
    },

    // Hide or show fields
    showCounter: {
      type: Boolean,
      default: true,
    },
    showMessage: {
      type: Boolean,
      default: true,
    },
    showPhoneNumber: {
      type: Boolean,
      default: true,
    },
    showCountry: {
      type: Boolean,
      default: false,
    },
    showState: {
      type: Boolean,
      default: false,
    },
    showPostcode: {
      type: Boolean,
      default: true,
    },

    // Mandatory Fields
    isPhoneMandatory: {
      type: Boolean,
      default: true,
    },

    // Buttons positions and contents
    signButtonValue: {
      type: String,
      default: "Submit",
    },
    viewMessageButtonValue: {
      type: String,
      default: "Here's the message we're sending",
    },
    viewMessagePosition: {
      type: String,
      default: "bottom",
    },

    stepupId: {
      type: String,
      required: false,
    },
  },
  data: function () {
    return {
      utmSourceParam: "",
      startcounter: 0,
      currentCounter: 0,
      firstName: "",
      lastName: "",
      email: "",
      postcode: "",
      phoneNumber: "",
      state: "",
      country: "Australia",
      message: "",
      isPure: true,
      isShake: false,
      countriesList: getCountriesList(),
      letterContent: null,
      timeout: null,
      isSaving: false,
      isFromRokt: false,
      origin: null,
      emailCheckSensis: {
        timeout: null,
        lastTimestamp: Date.now(),
        checkEmailLoading: false,
        emailRegex: /.+\@.+\..+/,
      },
      progress: 0,
      curr: 100,
      subscriptions: [],

      formTouched: false
    };
  },
  methods: {
    onEmailInput: function () {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      // Only call if not input for 500 ms
      this.timeout = setTimeout(() => {
        this.checkEmailDuplicate()
          .then((result) => {
            if (!result.valid) {
              this.errors.add({
                field: "email",
                msg: "Your email has already been submitted",
              });
            }
          })
          .catch((err) => {
            // If it fails, let the user free to continue
            console.log(err);
          });
      }, 500);
    },
    checkEmailDuplicate: function () {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("checkEmail", {
            campaignId: this.datalayerName,
            email: this.email,
          })
          .then((result) => {
            if (result) {
              resolve({
                valid: false,
                field: "email",
                data: { message: "Your email has already been submitted" },
              });
            } else {
              resolve({ valid: true });
            }
          });
      });
    },
    checkEmail: function () {
      return new Promise((resolve, reject) => {
        let timestamp = Date.now(); // timestamp for this api call
        this.emailCheckSensis.lastTimestamp = timestamp; // 1 , 2 , 3
        this.emailCheckSensis.checkEmailLoading = true;
        // Fetch Suggestions
        this.$store
          .dispatch("validateEmail", this.email)
          .then((response) => {
            if (response && timestamp >= this.emailCheckSensis.lastTimestamp) {
              this.emailCheckSensis.checkEmailLoading = false;
              resolve(response);
            }
          })
          .catch((err) => {
            console.error(err);
            this.emailCheckSensis.checkEmailLoading = false;
            // If it fails, let the user free to continue
            resolve({ valid: true });
          });
      });
    },
    updatePercentage(val) {
      const curr = Math.abs(val);

      if (curr === this.curr) {
        return;
      }

      this.curr = curr;
      let percent = (val / this.counterLimit) * 100;
      if (percent > 100) {
        percent = 100;
      }
      this.progress = percent;
    },
    openReadLetterPage: function () {
      this.$store.commit("setPopupContent", {
        title: this.letterTitle,
        message: this.letterContent,
        shareButton: false,
        recommendedReading: false,
        component: null,
        componentparams: null,
      });
      this.$store.commit("openPetitionMessagePopup", true);
    },
    save: function () {
      // Exit the function if it's busy
      if (this.isSaving) {
        return;
      }
      this.isSaving = true;

      // If we have a supplier for the phone not mandatory, use it, otherwise, use the default one
      const iveSupplier = this.isPhoneMandatory
        ? this.iveSupplier
        : this.iveSupplierPhoneNotMandatory
        ? this.iveSupplierPhoneNotMandatory
        : this.iveSupplier;

      const postBody = {
        email: this.email,
        firstName: this.firstName,
        lastName: this.lastName,
        postcode: !this.hidepostcode ? this.postcode : null,
        programName: this.programName,
        message: this.message ? this.message : "",
        cookieID: this.$cookie.get("_mkto_trk"),
        keepData: this.keepData,
        scheduledLead: this.scheduledLead,
        phone: this.phoneNumber,
        state: this.state,
        country: this.country,
        isFromRokt: this.isFromRokt,
        utmSource: this.utmSourceParam,
        origin: this.origin,
        sendToIve: this.sendToIve,
        iveSupplier: iveSupplier,
        iveSource: this.iveSource,
        ssoId: this.$store.getters.ssoId,
      };

      if (browser) {
        postBody.browsername = browser.name;
        postBody.browserversion = browser.version;
        postBody.browseros = browser.os;
      }

      this.$validator.validate().then(async (result) => {
        if (result) {
          // If we have a message, purify it
          if (this.showMessage && this.message && this.message.length > 0) {
            console.log("Purify Message");

            this.isPure = await this.$store.dispatch(
              "purifyText",
              this.message
            );

            if (this.isPure) {
              console.log("Message clean");
              this.saveLead(postBody);

              // Log step to Amplitude
              this.$store.dispatch("logPetitionSupporter", {
                id: postBody.programName,
                postcode: postBody.postcode
              });
            } else {
              console.log("Message not clean");
              this.isSaving = false;
            }
          } else {
            this.saveLead(postBody);

            // Log step to Amplitude
            this.$store.dispatch("logPetitionSupporter", {
              id: postBody.programName,
              postcode: postBody.postcode
            });
          }
        } else {
          console.log("Form invalid");
          // GTM Tracking
          if (window.dataLayer != undefined) {
            window.dataLayer.push({
              formName: this.datalayerName,
              event: "formSubmitEvent",
            });
          }
          this.isSaving = false;
          this.isShake = true;

          const el = document.getElementById("form-scroll-to");
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          }

          setTimeout(
            function () {
              this.isShake = false;
            }.bind(this),
            500
          );
        }
      });
    },
    saveLead: async function (postBody) {
      try {
        await this.$store.dispatch("saveLead", {
          campaignId: this.datalayerName,
          lead: postBody,
        });

        console.log("Petition - submit success");

        if (window.dataLayer != undefined) {
          window.dataLayer.push({
            formName: this.datalayerName,
            event: "formSubmitSuccessEvent",
          });
        }

        // Reset form
        this.firstName = "";
        this.lastName = "";
        this.email = "";
        this.postcode = "";
        this.message = "";
        this.phoneNumber = "";
        this.state = "";
        this.country = "Australia";
        this.isSaving = false;

        // Open thank you popup
        this.openThankYouPopup();

        // Log registration to Amplitude
        this.$store.dispatch("logPetitionComplete", {
          id: this.programName,
          count: this.currentCounter
        });
      } catch (err) {
        console.error("Petition submit failed");
        this.isSaving = false;
      }
    },
    openThankYouPopup() {
      const stepupId = getParameterByName("stepup") || this.stepupId;
      // Check query params for step up override
      if (stepupId) {
        this.$store
          .dispatch("getContentMessageById", stepupId)
          .then((response) => {
            const title = response.description;
            const content = response.content;

            this.$store.commit("setPopupContent", {
              component: "step-up",
              componentparams: {
                id: stepupId,
                title,
                content,
              },
            });
            
            this.$store.commit("openStepUpPopup", true);

            // Log to amplitude
            this.$store.dispatch("logStepUp", stepupId);
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        // If no step up, continue with thank you popup
        this.$store.commit("setPopupContent", {
          title: this.thankYouTitle,
          message: this.thankYouMessage,
          shareButton: true,
          recommendedReading: false,
        });
        this.$store.commit("openPetitionPopup", true);
      }
    },
    logFirstTouch(fieldName) {
      if (!this.formTouched) {
        this.$store.dispatch("logPetitionBegin", {
          id: this.programName,
          fieldName,
          count: this.currentCounter
        });
        this.formTouched = true;
      }
    }
  },
};
</script>

<style scoped>
h5,
p {
  color: var(--wwfWhite);
}

span.wwf-alert {
  position: absolute;
  display: block;
  text-align: left;
  width: 100%;
  pointer-events: none;
  z-index: 9;
}

span.wwf-alert p {
  position: relative;
  background-color: var(--errorColor);
  left: 0;
  top: -18px;
  display: inline-block;
  margin: 0 auto;
  text-align: center;
  padding: 5px 10px;
  line-height: normal;
  border-radius: 3px;
  color: var(--wwfWhite);
  font-size: 13px;
  box-shadow: 0 3px 13px var(--wwfTransparentBlack020);
  z-index: 10;
  font-weight: 600;
}

.btn-submit:disabled {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.input-loading {
  top: 6px;
}

.petition-form.wwf-form input[type="button"]:hover {
  background-color: var(--wwfDarkBlue);
  color: var(--wwfWhite);
}

.petition-form.wwf-form input.read-letter {
  background-color: var(--wwfWhite);
  font-family: "Lato", sans-serif;
  color: var(--wwfRegenerateAustralia);
  mix-blend-mode: normal;
  border: 1px solid var(--wwfRegenerateAustralia);
  box-sizing: border-box;
  border-radius: 3px;
}

.petition-form.wwf-form input[type="button"].read-letter:hover {
  color: var(--wwfRegenerateAustralia);
  background-color: var(--wwfWhite);
  border: 1px solid var(--wwfRegenerateAustralia);
}

.petition-form.wwf-form {
  padding: 20px;
}

.wwf-form.light input,
.wwf-form.light textarea,
.wwf-form.light select {
  padding: 12px 16px;
  font-size: 15px;
  background-color: var(--wwfWhite);
  border: 1px solid var(--wwfGreyA7);
  border-color: var(--wwfGreyC8);
}

.wwf-form.light input::placeholder,
.wwf-form.light textarea::placeholder,
.wwf-form.light select::placeholder {
  color: var(--wwfGrey82);
}

.wwf-form .word-count {
  color: var(--wwfGrey82);
}

.wwf-form input[type="button"].btn-submit {
  height: 48px;
  padding: 18px;
  font-family: "Open sans";
  font-weight: 500;
  color: var(--wwfWhite);
  letter-spacing: 1px;
  background-color: var(--wwfRegenerateAustralia);
  border: 0;
}

.wwf-form.light input[type="button"]:hover {
  background-color: var(--wwfGreyA7);
}

.petition-counter {
  margin: -20px -20px 40px;
  padding: 40px 40px 0px;
  border-bottom: 1px solid var(--wwfGreyDE);
  background-color: var(--wwfRegenerateAustralia);
}

.petition-counter hr {
  border-color: var(--wwfRegenerateFontColor);
  border: 1px solid var(--wwfWhite);
  opacity: 0.4;
}

.petition-counter h2,
.petition-counter h5 {
  width: 100%;
  color: var(--wwfWhite);
  text-align: center;
}

.petition-counter h2 {
  font-size: 100px;
}

.petition-counter h5 {
  font-size: 20px;
  line-height: 30px;
  margin-top: 20px;
  margin-bottom: 40px;
  font-weight: 600;
  letter-spacing: 1px;
  font-family: "Lato", sans-serif;
}

/* PROGRESS BAR */
.edo-progress-bar {
  position: relative;
  height: 64px;
}

.edo-tree-icon {
  position: absolute;
  width: 64px;
  height: 64px;
  top: 0;
  left: -32px;
}

.progress-bar-wrapper {
  position: absolute;
  width: 100%;
  height: 14px;
  padding: 3px;
  margin: 25px 0;
  background-color: var(--wwfWhite);
  border-radius: 7px;
}

.progress-bar-inner {
  float: right;
  background-color: var(--wwfRegenerateAustralia);
  width: 100%;
  height: 100%;
  border-radius: 7px;
}

/* There is global style for .wwf-form input with !important, overriding it locally here */
.petition-form.wwf-form input.read-letter {
  padding: 10px 4px!important;
}

@media only screen and (max-width: 40em) {
  .petition-counter {
    margin: -20px -20px 40px;
    padding: 20px 20px 0px;
  }

  .petition-form.wwf-form {
    padding: 0;
  }

  .edo-progress-bar {
    max-width: 250px;
    margin: 0 auto;
  }
}

@media only screen and (max-width: 374px) {
  .petition-form.wwf-form input.read-letter {
    font-size: 12px;
  }

  .edo-progress-bar {
    max-width: 200px;
  }
}
</style>


// WEBPACK FOOTER //
// PetitionBio.vue?1684af8e